import { type ReactElement, useId } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { getMessage } from '@/shared/copy/messages';
import { Box, DialogLayout, Inline, Panel, Stack, Text } from '@/ui';
import { Alert, Button, TextField } from '@/ui/primitives';
import type { UserProfileFormState } from '@/features/users/hooks/useUserProfileForm';

type UserProfileFormProps = { fieldDirection: 'row' | 'column'; profile: UserProfileFormState };

export const UserProfileForm = ({
  fieldDirection,
  profile,
}: UserProfileFormProps): ReactElement => {
  const firstNameId = useId();
  const lastNameId = useId();
  const emailId = useId();

  return (
    <Panel
      title="Profile"
      actions={
        <Button
          variant="contained"
          disableElevation
          fullWidthOnMobile={false}
          disabled={profile.saveDisabled}
          aria-label="Update profile"
          onClick={profile.onSave}
        >
          {getMessage('account.profile.updateCta')}
        </Button>
      }
    >
      <Stack gap="md">
        {profile.panelAlertMessage ? (
          <Alert severity="error" color="error" variant="outlined" sx={{ width: '100%' }}>
            {profile.panelAlertMessage}
          </Alert>
        ) : null}
        <Stack direction={fieldDirection} gap="md">
          <Box sx={{ flex: 1 }}>
            <TextField
              id={firstNameId}
              label="First Name"
              value={profile.firstName}
              onChange={(event) => {
                profile.onFirstNameChange(event.target.value);
              }}
              fullWidth
              disabled={profile.isLoading}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              id={lastNameId}
              label="Last Name"
              value={profile.lastName}
              onChange={(event) => {
                profile.onLastNameChange(event.target.value);
              }}
              fullWidth
              disabled={profile.isLoading}
            />
          </Box>
        </Stack>
        <Box>
          <TextField
            id={emailId}
            label={getMessage('account.profile.emailLabel')}
            type="email"
            value={profile.email}
            onChange={(event) => {
              profile.onEmailChange(event.target.value);
            }}
            fullWidth
            disabled={profile.isLoading || profile.emailReadOnly}
          />
          {profile.emailReadOnly ? (
            <Text role="captionSmall" color="textSecondary">
              {getMessage('account.profile.emailManagedByProvider')}
            </Text>
          ) : null}
        </Box>
        {!profile.emailVerifiedAt ? (
          <Inline gap="md" align="center" wrap="wrap">
            <Button
              variant="outlined"
              disableElevation
              fullWidthOnMobile={false}
              disabled={profile.resendVerificationDisabled}
              onClick={profile.onResendVerification}
            >
              {getMessage('account.profile.resendVerificationCta')}
            </Button>
          </Inline>
        ) : null}
      </Stack>
      <DialogLayout
        open={profile.verificationDialogOpen}
        onClose={profile.onVerificationCancel}
        maxWidth="xs"
      >
        <Stack gap="lg">
          <Stack gap="sm" alignItems="center">
            <Text role="sectionTitle" sx={{ textAlign: 'center' }}>
              {getMessage('account.profile.verifyEmailTitle')}
            </Text>
            <Text role="body" color="textSecondary" sx={{ textAlign: 'center' }}>
              {getMessage('account.profile.verifyEmailDescription', {
                email: profile.verificationTargetEmail,
              })}
            </Text>
          </Stack>
          {profile.verificationError ? (
            <Text role="captionSmall" color="error" sx={{ textAlign: 'center' }}>
              {profile.verificationError}
            </Text>
          ) : null}
          <Stack gap="md">
            <TextField
              label={getMessage('account.profile.verifyEmailCodeLabel')}
              placeholder={getMessage('account.profile.verifyEmailCodePlaceholder')}
              value={profile.verificationCode}
              onChange={(event) => {
                profile.onVerificationCodeChange(event.target.value);
              }}
              fullWidth
              disabled={profile.verificationSubmitting || profile.verificationSendPending}
            />
            <Button
              variant="primary"
              onClick={profile.onVerificationSubmit}
              disabled={
                profile.verificationCode.trim().length === 0 ||
                profile.verificationSubmitting ||
                profile.verificationSendPending
              }
              loading={profile.verificationSubmitting}
            >
              {getMessage('account.profile.verifyEmailSubmit')}
            </Button>
          </Stack>
          <Text role="body" color="textSecondary" sx={{ textAlign: 'center' }}>
            {getMessage('account.profile.didNotReceiveCode')}{' '}
            {profile.verificationResendOnCooldown ? (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {getMessage('account.profile.resendIn', {
                  seconds: profile.verificationResendSecondsRemaining,
                })}
              </Box>
            ) : (
              <Box
                component="button"
                type="button"
                onClick={profile.onVerificationResendCode}
                disabled={profile.verificationSubmitting || profile.verificationSendPending}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  color: 'primary.light',
                  cursor: 'pointer',
                  font: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {getMessage('account.profile.resend')}
              </Box>
            )}
          </Text>
        </Stack>
      </DialogLayout>
      <ConfirmDialog
        open={profile.reverificationDialogOpen}
        title={getMessage('account.profile.reverifySessionTitle')}
        description={getMessage('account.profile.reverifySessionDescription')}
        confirmLabel={getMessage('account.profile.reverifyContinue')}
        confirmColor="primary"
        confirmDisabled={profile.reverificationPassword.trim().length === 0}
        loading={profile.reverificationSubmitting}
        onClose={profile.onReverificationCancel}
        onConfirm={profile.onReverificationSubmit}
      >
        <Stack gap="md">
          <TextField
            label="Current Password"
            type="password"
            value={profile.reverificationPassword}
            onChange={(event) => {
              profile.onReverificationPasswordChange(event.target.value);
            }}
            fullWidth
            disabled={profile.reverificationSubmitting}
          />
          {profile.reverificationError ? (
            <Text role="captionSmall" color="error">
              {profile.reverificationError}
            </Text>
          ) : null}
        </Stack>
      </ConfirmDialog>
    </Panel>
  );
};
